import { useQuery } from '@tanstack/react-query'
import { createApiCall } from 'services'
import type { CompanyUser } from 'types'
import { useMemo } from 'react'

interface CompanyUsersResponse {
  data: CompanyUser[]
  total: number
  limit: number
  offset: number
}

type UseCompanyUsersParams = {
  id?: string
  limit?: number
  offset?: number
}

type UseCompanyUsersResult = {
  data: CompanyUsersResponse | null
  isLoading: boolean
  isError: boolean
  error: Error | null
  refetch: () => void
}

export const useCompanyUsersQuery = (params?: UseCompanyUsersParams, options?: { enabled?: boolean }): UseCompanyUsersResult => {
  const { id, ...rest } = params || {}

  const stableParams = useMemo(() => ({
    limit: rest.limit ?? 50,
    offset: rest.offset ?? 0,
  }), [rest.limit, rest.offset])

  const isEnabled = options?.enabled !== false

  const query = useQuery({
    queryKey: ['companies', 'users', id || 'me', stableParams],
    queryFn: async () => {
      // Use 'me' endpoint if no id is provided
      const url = id
        ? `/companies/admin/${id}/members`
        : `/companies/admin/me/members`

      const response = await createApiCall({
        method: 'GET',
        url,
        params: stableParams,
      })
      return response.data as CompanyUsersResponse
    },
    enabled: isEnabled,
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    placeholderData: (previousData) => previousData,
    gcTime: 1000 * 60 * 10, // 10 minutes
  })

  return {
    data: query.data || null,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  }
}

// Alias for backward compatibility
export const useCompanyUsers = useCompanyUsersQuery
